import { ArrowRight } from 'lucide-react';
import type { Experience, PackageGroup, PriceRow } from '../../data/experiences';
import { packageRowOptions } from '../../data/experiences';
import { whatsappLink } from '../../data/site';
import { trackWhatsAppClick } from '../../lib/analytics';
import { useBooking } from '../booking';

/** Joins the guest / duration meta into a single label, e.g. "2 people, 3 hours". */
function metaLabel(row: PriceRow): string {
  return [row.guests, row.duration].filter(Boolean).join(', ');
}

/** Swap the live total into the static price string, keeping "From", "/ couple" etc. */
function withLivePrice(staticPrice: string, total: number): string {
  const v = Number.isInteger(total) ? String(total) : total.toFixed(2);
  return staticPrice.replace(/€\s*\d[\d.,]*/, `€${v}`);
}

export function PackageGroupCard({
  group,
  experience,
  livePrice,
  pricingStatus = 'idle',
}: {
  group: PackageGroup;
  experience: Experience;
  livePrice?: (optionAlias: string, pax: number) => number | null;
  pricingStatus?: 'idle' | 'loading' | 'ready' | 'error';
}) {
  const { openBooking } = useBooking();
  const rowOptions = packageRowOptions[group.id];
  return (
    <article
      id={group.id}
      className="scroll-mt-28 overflow-hidden rounded-2xl bg-white shadow-card"
    >
      {/* Hidden aliases so secondary anchors (e.g. #anniversary-photoshoot) scroll here too */}
      {group.aliases?.map((alias) => (
        <span key={alias} id={alias} className="block scroll-mt-28" aria-hidden="true" />
      ))}

      <div className="border-b border-charcoal/10 px-6 pb-6 pt-7 sm:px-8">
        <h3 className="font-display text-2xl font-medium text-charcoal">{group.title}</h3>
        {group.description && (
          <p className="mt-2 max-w-2xl font-sans text-sm font-light leading-relaxed text-stone">
            {group.description}
          </p>
        )}
      </div>

      {group.rows.length > 0 ? (
        <ul className="divide-y divide-charcoal/10">
          {group.rows.map((row, i) => {
            const opt = rowOptions?.[i];
            const live = opt && livePrice ? livePrice(opt.alias, opt.pax) : null;
            // No static price fallback: a price shows only when it's live.
            const priceLoading =
              !!opt &&
              live == null &&
              (pricingStatus === 'idle' || pricingStatus === 'loading');
            const canBook = live != null;
            return (
            <li
              key={i}
              className="flex flex-col gap-4 px-6 py-6 sm:flex-row sm:items-center sm:gap-8 sm:px-8"
            >
              <div className="flex-1">
                {metaLabel(row) && (
                  <p className="font-sans text-[0.7rem] font-semibold uppercase tracking-wide2 text-crimson">
                    {metaLabel(row)}
                  </p>
                )}
                <p className="mt-1.5 font-sans text-sm font-light leading-relaxed text-stone">
                  {row.includes}
                </p>
              </div>

              <div className="flex items-center justify-between gap-5 sm:flex-col sm:items-end sm:justify-center sm:gap-3">
                {canBook || priceLoading ? (
                  <>
                    <span
                      className={`font-display text-2xl font-medium ${
                        canBook ? 'text-charcoal' : 'text-charcoal/25'
                      }`}
                    >
                      {canBook ? withLivePrice(row.price, live as number) : '€…'}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        openBooking(experience, undefined, 'package_card', opt?.alias)
                      }
                      className="group/link inline-flex items-center gap-1.5 font-sans text-[0.68rem] font-medium uppercase tracking-wide2 text-charcoal transition-colors hover:text-crimson"
                    >
                      Reserve
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/link:translate-x-1" />
                    </button>
                  </>
                ) : (
                  <a
                    href={whatsappLink(
                      `Hi Flying Muse! I'd like to book the ${group.title}${
                        metaLabel(row) ? ` (${metaLabel(row)})` : ''
                      }. Could you share availability and pricing?`,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                    // Breakage, not intent: this row has no live price, so the
                    // guest could not book it even though they wanted to.
                    onClick={() =>
                      trackWhatsAppClick('package_card', experience, group.title)
                    }
                    className="group/link inline-flex items-center gap-1.5 font-sans text-[0.68rem] font-medium uppercase tracking-wide2 text-charcoal transition-colors hover:text-crimson"
                  >
                    Contact on WhatsApp
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/link:translate-x-1" />
                  </a>
                )}
              </div>
            </li>
            );
          })}
        </ul>
      ) : (
        <div className="px-6 py-7 sm:px-8">
          <p className="font-sans text-sm font-light leading-relaxed text-stone">{group.note}</p>
          <a
            href={whatsappLink(`Hi Flying Muse! I'd like to ask about the ${group.title} (${experience.pageTitle}).`)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackWhatsAppClick('package_enquiry', experience, group.title)
            }
            className="group/link mt-4 inline-flex items-center gap-1.5 font-sans text-[0.68rem] font-medium uppercase tracking-wide2 text-charcoal transition-colors hover:text-crimson"
          >
            Enquire
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/link:translate-x-1" />
          </a>
        </div>
      )}

      {group.rows.length > 0 && group.note && (
        <p className="border-t border-charcoal/10 px-6 py-5 font-sans text-xs font-light italic text-stone sm:px-8">
          {group.note}
        </p>
      )}
    </article>
  );
}
